import * as React from 'react';
import { useMemo } from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { AdminStats, SubscriptionData } from '../../models';
import { Button, Tooltip, withStyles } from '@material-ui/core';
import { OverviewCard, OverviewHeader, OverviewDetails, OverviewDetail, OverviewActions, OverviewUsageSummary } from './styled';
import { formatCurrency, formatDateLocalized } from './utils';
import SubscriptionHistoryModal from './SubscriptionHistoryModal';

const StyledTooltip = withStyles({
    tooltip: {
        fontSize: '14px',
        padding: '8px 12px',
    },
})(Tooltip);

interface SubscriptionOverviewProps {
    currentSubscription: SubscriptionData | null | undefined;
    nextSubscription: SubscriptionData | null | undefined;
    currentMonthlyTotal: number | undefined;
    usageStats: AdminStats | null;
    isHistoryModalOpen: boolean;
    onHistoryModalOpen: () => void;
    onHistoryModalClose: () => void;
}

const formatStorageInGb = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return gb >= 10 ? gb.toFixed(0) : gb.toFixed(1);
};

const SubscriptionOverview: React.FC<SubscriptionOverviewProps> = ({ currentSubscription, currentMonthlyTotal, nextSubscription, usageStats, isHistoryModalOpen, onHistoryModalOpen, onHistoryModalClose }) => {
    const t = useTranslations();

    const planDisplayName = useMemo(() => {
        if (!currentSubscription?.subscriptionName) return t(TK.notAvailable);
        return currentSubscription.isTrial ? t(TK.trial) : currentSubscription.subscriptionName;
    }, [currentSubscription, t]);

    const paymentValidity = currentSubscription?.paymentValidityDate 
            && currentSubscription.endDate
            && currentSubscription.paymentValidityDate.substring(0, 10) === currentSubscription.endDate.substring(0, 10) 
            && nextSubscription?.paymentValidityDate 
        ? nextSubscription.paymentValidityDate
        : currentSubscription?.paymentValidityDate

    const usageSummary = useMemo(() => {
        if (!usageStats) return null;

        const storageUsedBytes = usageStats.documentsStorage.usedBytes + usageStats.databaseStorage.usedBytes;
        const storageAllowedBytes = usageStats.documentsStorage.allowedBytes + usageStats.databaseStorage.allowedBytes;
        const parts = [
            `SMS ${usageStats.totalSmssSent.thisMonth}/${usageStats.allowedSmss}`,
            `WhatsApp ${usageStats.totalWhatsAppsSent.thisMonth}/${usageStats.allowedWhatsApps}`,
            `Storage ${formatStorageInGb(storageUsedBytes)}/${formatStorageInGb(storageAllowedBytes)} GB`,
        ];

        return parts.join(' • ');
    }, [usageStats]);

    return (
        <OverviewCard>
            <OverviewHeader>
                <h2>{t(TK.currentSubscriptionOverview)}</h2>
            </OverviewHeader>

            <OverviewDetails>
                <OverviewDetail>
                    <span className="label">{t(TK.currentPlan)}:</span>
                    <span className="value plan-name">{planDisplayName?.toUpperCase() ?? 'N/A'}</span>
                </OverviewDetail>

                { !currentSubscription?.isTrial && <>
                    <OverviewDetail>
                        <span className="label">{t(TK.monthlyTotal)}:</span>
                        <span className="value price">
                            {currentMonthlyTotal !== undefined ? formatCurrency(currentMonthlyTotal) : t(TK.notAvailable)}
                        </span>
                    </OverviewDetail>

                    <OverviewDetail>
                        <span className="label">{t(TK.lastPaymentDate)}:</span>
                        <span className="value">{formatDateLocalized(currentSubscription?.lastPaymentDate, t(TK.notAvailable))}</span>
                    </OverviewDetail>
                </>}

                <OverviewDetail>
                    <span className="label">{t(TK.nextPaymentDate)}:</span>
                    <span className="value">{formatDateLocalized(paymentValidity, t(TK.notAvailable))}</span>
                </OverviewDetail>
            </OverviewDetails>

            {usageSummary && (
                <OverviewUsageSummary>{usageSummary}</OverviewUsageSummary>
            )}

            <OverviewActions>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onHistoryModalOpen}
                >
                    {t(TK.viewInvoicesAndPayments)}
                </Button>
            </OverviewActions>

            <SubscriptionHistoryModal
                isOpen={isHistoryModalOpen}
                onClose={onHistoryModalClose}
            />
        </OverviewCard>
    );
};

export default SubscriptionOverview;
